import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http, wsUrl, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, LogIn, Smile, ImagePlus, X, Loader2, Trash2, VolumeX, Lock } from "lucide-react";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import { toast } from "sonner";

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

// Convert URLs in plain text into clickable <a> tags. Returns React fragments.
const URL_RE = /(https?:\/\/[^\s]+)/gi;
function linkify(text) {
  if (!text) return null;
  const parts = text.split(URL_RE);
  return parts.map((p, i) => {
    if (URL_RE.test(p)) {
      URL_RE.lastIndex = 0; // reset stateful regex
      const safe = p.replace(/[.,;!?)]+$/g, "");
      const trail = p.slice(safe.length);
      return (
        <React.Fragment key={i}>
          <a
            href={safe}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 break-all"
          >
            {safe}
          </a>
          {trail}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function ChatImage({ imageId, mine }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("popoyo_token");
    if (!imageId || !token) return;
    const url = `${API}/chat/files/${imageId}?auth=${encodeURIComponent(token)}`;
    queueMicrotask(() => setSrc(url));
  }, [imageId]);
  if (!src) return null;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="block">
      <img
        src={src}
        alt="chat attachment"
        loading="lazy"
        className={`mt-1 rounded-xl max-h-72 max-w-full object-cover ${mine ? "ring-2 ring-white/30" : "ring-1 ring-[color:var(--hairline)]"}`}
      />
    </a>
  );
}

export default function ChatBox({ onOpenAuth }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();

  const channelsQ = useQuery({
    queryKey: ["chat-channels"],
    queryFn: async () => (await http.get("/chat/channels")).data,
    staleTime: 60 * 60_000,
  });

  const channels = channelsQ.data || [{ id: "general", name: "General Popoyo", emoji: "🌴" }];
  const [channel, setChannel] = useState(() => localStorage.getItem("popoyo_channel") || "general");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { file_id, preview_url, uploading }
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiBoxRef = useRef(null);

  useEffect(() => { localStorage.setItem("popoyo_channel", channel); }, [channel]);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmoji) return;
    const handler = (e) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(e.target)) {
        queueMicrotask(() => setShowEmoji(false));
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmoji]);

  // Load history when channel changes
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => setMessages([]));
    http.get("/chat/history", { params: { channel, limit: 80 } })
      .then((r) => { if (!cancelled) queueMicrotask(() => setMessages(r.data)); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [channel]);

  // (re)connect WS when user or channel changes
  useEffect(() => {
    if (!user || !user.id) {
      try { wsRef.current?.close(); } catch (_e) { /* ignore */ }
      queueMicrotask(() => setConnected(false));
      return;
    }
    const url = new URL(wsUrl("/api/chat/ws"));
    url.searchParams.set("channel", channel);
    const ws = new WebSocket(url.toString());
    wsRef.current = ws;
    ws.onopen = () => queueMicrotask(() => setConnected(true));
    ws.onclose = () => queueMicrotask(() => setConnected(false));
    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.kind === "message") {
          queueMicrotask(() => setMessages((prev) => [...prev, data.message]));
        } else if (data.kind === "chat_delete") {
          queueMicrotask(() => setMessages((prev) => prev.filter((m) => m.id !== data.id)));
        } else if (data.kind === "muted") {
          const until = data.until ? new Date(data.until).toLocaleString() : "";
          toast.error(`You’re muted until ${until}${data.reason ? ` — "${data.reason}"` : ""}`, { duration: 6000 });
        }
      } catch (_e) { /* ignore */ }
    };
    return () => { try { ws.close(); } catch (_e) { /* ignore */ } };
  }, [user?.id, channel]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const onPickEmoji = (emoji) => {
    const v = emoji.emoji;
    setText((prev) => prev + v);
    inputRef.current?.focus();
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Max 5 MB");
      return;
    }
    const preview = URL.createObjectURL(file);
    setPendingImage({ file_id: null, preview, uploading: true });
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await http.post("/chat/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      queueMicrotask(() => setPendingImage({ file_id: data.file_id, preview, uploading: false }));
    } catch (err) {
      queueMicrotask(() => setPendingImage(null));
      URL.revokeObjectURL(preview);
      alert(t("upload_failed"));
    }
  };

  const send = (e) => {
    e?.preventDefault?.();
    const v = text.trim();
    if (!v && !pendingImage?.file_id) return;
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ text: v, image_id: pendingImage?.file_id || null }));
    setText("");
    if (pendingImage?.preview) URL.revokeObjectURL(pendingImage.preview);
    setPendingImage(null);
    setShowEmoji(false);
  };

  const currentChannel = channels.find((c) => c.id === channel) || channels[0];

  return (
    <section data-testid="chat-box" className="clay p-6 md:p-7 flex flex-col" style={{ minHeight: 560 }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="eyebrow">{t("chat")}</p>
          <h3 data-testid="chat-channel-name" className="text-2xl font-semibold mt-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <span className="text-2xl">{currentChannel?.emoji}</span>
            <span className="truncate">{currentChannel?.name}</span>
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-[color:var(--ink-2)]">
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[color:var(--teal)] animate-pulse" : "bg-[color:var(--ink-2)]/40"}`} />
          {connected ? t("live_label") : "—"}
          <MessageCircle className="w-4 h-4 text-[color:var(--navy)]" />
        </div>
      </div>

      {/* Channel pills */}
      <div data-testid="chat-channels" className="flex flex-wrap gap-1.5 mb-3">
        {channels.map((c) => (
          <button
            key={c.id}
            data-testid={`chat-channel-${c.id}`}
            onClick={() => setChannel(c.id)}
            className={`pill px-3 py-1 text-xs font-medium transition-colors ${
              c.id === channel
                ? "bg-[color:var(--navy)] text-white"
                : "bg-[color:var(--sand-2)] text-[color:var(--ink)] hover:bg-[color:var(--sand-2)]/70"
            }`}
          >
            <span className="mr-1">{c.emoji}</span>{c.name}
          </button>
        ))}
      </div>

      <div ref={listRef} data-testid="chat-messages" className="flex-1 overflow-y-auto thin-scroll space-y-2 py-2 pr-1 min-h-[300px]">
        {!user?.id ? (
          <div
            className="h-full min-h-[300px] flex flex-col items-center justify-center text-center gap-3 px-6 py-10 rounded-2xl bg-[color:var(--sand-2)]/60 border border-[color:var(--ink-2)]/10"
            data-testid="chat-signin-gate"
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: "var(--navy)" }}
            >
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>
                Sign in to see the chat
              </p>
              <p className="text-sm text-[color:var(--ink-2)] mt-1 max-w-sm">
                Our community chat is private — free account needed to read and send messages.
              </p>
            </div>
            <Button
              onClick={onOpenAuth}
              className="pill bg-[color:var(--coral)] text-white hover:opacity-90"
              data-testid="chat-signin-btn"
            >
              <LogIn className="w-4 h-4 mr-1.5" /> Sign in to view
            </Button>
          </div>
        ) : (
          <>
            {messages.length === 0 && (
              <p className="text-sm text-[color:var(--ink-2)] italic text-center mt-12">
                {t("chat_empty")}
              </p>
            )}
            {messages.map((m) => {
              const mine = user?.id && m.user_id === user.id;
              const canDelete = user?.id && (mine || user.role === "admin");
              const canMute = user?.role === "admin" && !mine && m.user_id;
              const doDelete = async () => {
                if (!window.confirm("Delete this message?")) return;
                try {
                  await http.delete(`/chat/messages/${m.id}`);
                  queueMicrotask(() => setMessages((prev) => prev.filter((x) => x.id !== m.id)));
                } catch (e) {
                  alert("Could not delete message");
            }
          };
          const doMute = async () => {
            const mins = window.prompt(`Mute @${m.user_name} for how many minutes?`, "60");
            if (!mins) return;
            const minutes = parseInt(mins, 10);
            if (!Number.isFinite(minutes) || minutes < 1) { toast.error("Invalid duration"); return; }
            const reason = window.prompt("Optional reason (visible to admins only):", "") || "";
            try {
              await http.post(`/admin/users/${m.user_id}/mute`, { minutes, reason });
              toast.success(`Muted @${m.user_name} for ${minutes} min`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Mute failed");
            }
          };
          return (
            <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex items-end gap-2 group ${mine ? "justify-end" : "justify-start"}`}>
              {!mine && (
                <a href={`#user-${m.user_id}`} className="shrink-0" aria-label={m.user_name}>
                  {m.user_avatar_file_id ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${m.user_avatar_file_id}`} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <span className="w-7 h-7 rounded-full bg-[color:var(--sand-2)] text-[color:var(--ink-2)] text-[10px] font-bold uppercase flex items-center justify-center">
                      {(m.user_name || "?").slice(0, 1)}
                    </span>
                  )}
                </a>
              )}
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${mine ? "bg-[color:var(--navy)] text-white" : "bg-[color:var(--sand-2)] text-[color:var(--ink)]"}`}>
                <p className={`text-[10px] uppercase tracking-widest mb-0.5 ${mine ? "text-white/70" : "text-[color:var(--ink-2)]"}`}>
                  {m.user_name} · {fmtTime(m.created_at)}
                </p>
                {m.text && (
                  <p className="text-sm leading-snug whitespace-pre-wrap break-words">{linkify(m.text)}</p>
                )}
                {m.image_id && <ChatImage imageId={m.image_id} mine={mine} />}
              </div>
              {canDelete && (
                <button
                  data-testid={`chat-delete-${m.id}`}
                  onClick={doDelete}
                  title="Delete message"
                  aria-label="Delete message"
                  className="opacity-0 group-hover:opacity-100 transition shrink-0 text-[color:var(--ink-2)] hover:text-[color:var(--emergency)] p-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              {canMute && (
                <button
                  data-testid={`chat-mute-${m.id}`}
                  onClick={doMute}
                  title={`Mute @${m.user_name} from chat`}
                  aria-label="Mute user"
                  className="opacity-0 group-hover:opacity-100 transition shrink-0 text-[color:var(--ink-2)] hover:text-[color:var(--emergency)] p-1"
                >
                  <VolumeX className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
          </>
        )}
      </div>

      {user?.id ? (
        <div className="mt-3 relative">
          {/* Pending image preview */}
          {pendingImage && (
            <div data-testid="chat-pending-image" className="mb-2 inline-flex items-center gap-2 bg-[color:var(--sand-2)] rounded-2xl pr-3 py-1.5 pl-1.5">
              <div className="relative w-12 h-12 rounded-xl overflow-hidden bg-black/10">
                <img src={pendingImage.preview} alt="preview" className="w-full h-full object-cover" />
                {pendingImage.uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                  </div>
                )}
              </div>
              <span className="text-xs text-[color:var(--ink-2)]">
                {pendingImage.uploading ? t("uploading") : t("ready_to_send")}
              </span>
              <button
                data-testid="chat-cancel-image"
                onClick={() => {
                  if (pendingImage.preview) URL.revokeObjectURL(pendingImage.preview);
                  setPendingImage(null);
                }}
                className="ml-1 text-[color:var(--ink-2)] hover:text-[color:var(--emergency)]"
                aria-label="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Emoji picker popover */}
          {showEmoji && (
            <div ref={emojiBoxRef} className="absolute bottom-full mb-2 left-0 z-20 shadow-2xl rounded-2xl overflow-hidden" data-testid="emoji-picker">
              <EmojiPicker
                onEmojiClick={onPickEmoji}
                emojiStyle={EmojiStyle.NATIVE}
                theme={Theme.LIGHT}
                width={Math.min(340, typeof window !== "undefined" ? window.innerWidth - 40 : 340)}
                height={380}
                searchDisabled={false}
                skinTonesDisabled
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}

          <form onSubmit={send} className="flex items-center gap-2">
            <button
              type="button"
              data-testid="chat-emoji-btn"
              onClick={() => setShowEmoji((s) => !s)}
              className={`pill w-9 h-9 flex items-center justify-center shrink-0 transition-colors ${
                showEmoji ? "bg-[color:var(--coral)] text-white" : "bg-[color:var(--sand-2)] text-[color:var(--ink-2)] hover:text-[color:var(--coral)]"
              }`}
              aria-label="Add emoji"
            >
              <Smile className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="chat-image-btn"
              onClick={() => fileInputRef.current?.click()}
              className="pill w-9 h-9 flex items-center justify-center shrink-0 bg-[color:var(--sand-2)] text-[color:var(--ink-2)] hover:text-[color:var(--coral)]"
              aria-label="Attach image"
            >
              <ImagePlus className="w-4 h-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={onPickFile}
              className="hidden"
              data-testid="chat-image-file"
            />
            <Input
              ref={inputRef}
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("type_message")}
              className="rounded-full bg-white flex-1"
              maxLength={600}
            />
            <Button
              data-testid="chat-send-btn"
              type="submit"
              disabled={(!text.trim() && !pendingImage?.file_id) || pendingImage?.uploading}
              className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral-2)]"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">{t("send")}</span>
            </Button>
          </form>
        </div>
      ) : (
        <div data-testid="chat-login-gate" className="mt-3 rounded-2xl bg-[color:var(--sand-2)] p-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-[color:var(--ink-2)]">
            <LogIn className="inline w-4 h-4 mr-1 mb-0.5" />
            {t("login_to_chat")}
          </p>
          <Button
            data-testid="chat-login-cta"
            onClick={() => onOpenAuth?.()}
            size="sm"
            className="pill bg-[color:var(--navy)] hover:bg-[color:var(--navy-2)] text-white"
          >
            {t("login")} / {t("register")}
          </Button>
        </div>
      )}
    </section>
  );
}
